import { useDebounce } from '@uidotdev/usehooks';
import { ClosingMethod as DeprecatedClosingMethod } from 'services/campaign/types';
import {
  ClosingMethod,
  useSaveCampaignMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { useEffect, useState } from 'react';
import { mapQuestionsPayloadV3 } from 'modules/campaign-creation/utils/mapQuestionsPayloadV3';
import { UpdateCampaignInput } from 'generated-graphql/types';
import { QuestionWithId } from 'pages/CampaignCreation/types';

type SaveStateCampaignParams = {
  outreachDescription?: string;
  title?: string;
  jobDescriptionText?: string;
  externalId?: string;
  jobDescriptionParsedJobTitle?: string;
  jobDescriptionParsedEmployerName?: string;
  jobDescriptionParsedLocation?: string;
  jobDescriptionParsedContractType?: string;
  additionalContext?: string;
  questions?: QuestionWithId[];
  agentId?: string;
  closingMethod?: DeprecatedClosingMethod;
  templateId?: string;
  customTemplatedMessage?: string;
  customTemplatedSubjectLine?: string;
  calendarMeetingTemplateId?: string;
  customRequest?: string;
  rejectionMessage?: string;
  closingMessage?: string;
  timeToAutoCloseConversationsInHours?: number;
};

const compareParams = (
  params1: SaveStateCampaignParams | null,
  params2: SaveStateCampaignParams | null
) => {
  return JSON.stringify(params1) === JSON.stringify(params2);
};

// NOTE: This is for saving the params that are submitted
// This is to avoid losing changes when the user makes a lot of changes in the length forms
// This is also to avoid saving the store changes, that are already saved on live update
export const useSaveStateCampaign = (params: SaveStateCampaignParams) => {
  const onChangeUpdateDate = useCampaignCreationStore(
    (store) => store.onChangeUpdateDate
  );
  const campaignId = useCampaignCreationStore((store) => store.campaignId);
  const [saveCampaignMutation] = useSaveCampaignMutation();
  const [savedParams, setSavedParams] =
    useState<SaveStateCampaignParams | null>(null);
  const debouncedParams = useDebounce(params, 2000);

  useEffect(() => {
    const saveDebouncedParams = async () => {
      if (
        !campaignId ||
        !debouncedParams ||
        compareParams(savedParams, debouncedParams)
      ) {
        return;
      }

      // NOTE: We only need to save the params, that aren't submitted
      // The steps that are saved on live update are:
      // - ENGAGEMENT_DESCRIPTION STEP
      // - NO_ATS_JOB_DESCRIPTION STEP
      // - EDIT_JOB_DESCRIPTION STEP
      // - QUESTIONS_AND_DOCUMENTS STEP
      // - SETUP_AND_SIMULATION STEP
      const input: UpdateCampaignInput = {
        id: campaignId
      };

      if (debouncedParams.outreachDescription) {
        input.outreachDescription = debouncedParams.outreachDescription;
      }

      if (debouncedParams.title) {
        input.title = debouncedParams.title;
      }

      if (debouncedParams.jobDescriptionText) {
        input.jobDescriptionText = debouncedParams.jobDescriptionText;
      }

      if (debouncedParams.externalId) {
        input.externalId = debouncedParams.externalId;
      }

      if (
        debouncedParams.jobDescriptionParsedJobTitle &&
        debouncedParams.jobDescriptionParsedEmployerName &&
        debouncedParams.jobDescriptionParsedLocation
      ) {
        input.jobDescriptionParsed = {
          jobTitle: debouncedParams.jobDescriptionParsedJobTitle,
          employerName: debouncedParams.jobDescriptionParsedEmployerName,
          location: debouncedParams.jobDescriptionParsedLocation,
          contractType: debouncedParams.jobDescriptionParsedContractType || null
        };
      }

      if (debouncedParams.additionalContext) {
        input.additionalContext = debouncedParams.additionalContext;
      }

      if (debouncedParams.questions) {
        input.questions = mapQuestionsPayloadV3(debouncedParams.questions);
      }

      if (debouncedParams.agentId) {
        input.agentId = debouncedParams.agentId;
      }

      if (debouncedParams.closingMethod) {
        // TODO: Fix this typing
        input.closingMethod =
          debouncedParams.closingMethod as unknown as ClosingMethod;
      }

      if (debouncedParams.templateId) {
        input.templateId = debouncedParams.templateId;
      }

      if (debouncedParams.customTemplatedMessage) {
        input.customTemplatedMessage = debouncedParams.customTemplatedMessage;
      }

      if (debouncedParams.customTemplatedSubjectLine) {
        input.customTemplatedSubjectLine =
          debouncedParams.customTemplatedSubjectLine;
      }

      if (debouncedParams.calendarMeetingTemplateId) {
        input.calendarMeetingTemplateId =
          debouncedParams.calendarMeetingTemplateId;
      }

      if (debouncedParams.customRequest) {
        input.customRequest = debouncedParams.customRequest;
      }

      if (debouncedParams.rejectionMessage) {
        input.rejectionMessage = debouncedParams.rejectionMessage;
      }

      if (debouncedParams.closingMessage) {
        input.closingMessage = debouncedParams.closingMessage;
      }

      if (debouncedParams.timeToAutoCloseConversationsInHours) {
        input.timeToAutoCloseConversationsInHours =
          debouncedParams.timeToAutoCloseConversationsInHours;
      }

      if (Object.keys(input).length <= 1) {
        return;
      }

      setSavedParams(debouncedParams);

      const result = await saveCampaignMutation({
        variables: {
          input
        }
      });

      if (result.data?.updateCampaign?.updatedAt) {
        onChangeUpdateDate();
      }
    };
    saveDebouncedParams();
  }, [debouncedParams]);

  return null;
};
